import { Link } from 'react-router-dom';

import { useCurrentUser, useView } from '@/hooks';
import { Icon, Typography } from '@/ui';

import HomeStatusBanner from './HomeStatusBanner';

import { useGetPulseReportsQuery } from '@/store/api/pulseApi';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const isSubmittedWithinLastMonth = (submittedAt?: string) => {
  if (!submittedAt) {
    return false;
  }

  const submittedTime = new Date(submittedAt).getTime();
  if (Number.isNaN(submittedTime)) {
    return false;
  }

  return submittedTime >= Date.now() - THIRTY_DAYS_MS;
};

interface OnboardingReviewBannerPureProps {
  reviewPath: string;
}

const OnboardingReviewBannerPure = ({
  reviewPath,
}: OnboardingReviewBannerPureProps) => (
  <section className="mb-8 w-full max-w-screen-lg px-4 md:px-0">
    <HomeStatusBanner
      action={
        <Link
          className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-xl bg-sage-500 px-4 py-1.5 text-center text-[13px] font-bold leading-6 tracking-[0.4px] text-white no-underline hover:bg-sage-600 sm:w-auto"
          to={reviewPath}
        >
          Review my responses
        </Link>
      }
      icon={
        <Icon
          className="mt-0.5 shrink-0 text-cement-500 sm:mt-0"
          fontSize="small"
        >
          check
        </Icon>
      }
      title={
        <Typography className="font-bold text-cement-500" variant="subtitle1">
          Coach matching questions and baseline complete
        </Typography>
      }
    />
  </section>
);

const OnboardingReviewBanner = () => {
  const { isClient } = useView();
  const { user, isUserLoading } = useCurrentUser();
  const hasExactlyOneAssignedCoach = user.coaches.length === 1;
  const shouldQuery =
    isClient && !isUserLoading && !!user.id && !hasExactlyOneAssignedCoach;

  const { data } = useGetPulseReportsQuery(
    {
      formType: 'member_onboarding',
      memberId: user.id || '',
      onboardingReviewAvailable: true,
      page: 1,
      per: 1,
    },
    { skip: !shouldQuery },
  );

  const onboardingReport = data?.reports.find(
    (report) => report.isOnboardingBaseline === true,
  );

  if (
    !shouldQuery ||
    !onboardingReport ||
    !isSubmittedWithinLastMonth(onboardingReport.submittedAt)
  ) {
    return null;
  }

  return (
    <OnboardingReviewBannerPure
      reviewPath={`/m/onboarding-review/${onboardingReport.reportId}`}
    />
  );
};

export { isSubmittedWithinLastMonth, OnboardingReviewBannerPure };
export type { OnboardingReviewBannerPureProps };
export default OnboardingReviewBanner;
